import { useQuery } from '@apollo/client';
import InputDate from '@components/InputDate';
import InputStyles from '@components/InputFieldControlled/InputText.styles';
import InputMultiSelect from '@components/InputMultiSelect';
import InputTime from '@components/InputTime';
import Popover from '@components/Popover';
import Text from '@components/Text';
import { GET_TALENT_PROFESSIONS_QUERY } from '@gql/talentProfessions';
import PremiumOfferDialog from '@modules/subscriptionPlan/PremiumOfferDialog';
import OptionItems, { OptionItem, permiumOptions, PremiumOption } from '@modules/subscriptionPlan/PremiumOptions';
import { useJobOfferCredits } from '@modules/subscriptionPlan/useJobOfferCredits';
import { Box, FormGroup, FormLabel, Grid, Stack, TextField, Typography, useMediaQuery } from '@mui/material';
import { moment } from '@utils/date.utils';
import React, { useEffect, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import InputSwitchControlled from '@components/InputSwitch/InputSwitchControlled';
import { useOptionsContext } from '@modules/talents/context/options-context';
import AssignJobOfferButton from '../../components/AssignJobOfferButton';
import AddMore from '../../components/AddMore';
import AddMoreWithReorder from '../../components/AddMoreWithReorder';

const premiumUsers = process.env.NEXT_PUBLIC_PREMIUM_USERS || '';

type Props = {
    pro: any;
};

const JobOfferRecruitmentForm = ({ pro }: Props) => {

    const { data: optionsData } = useOptionsContext();
    const options = useMemo(() => optionsData?.options, [optionsData]);
    const { control, watch, setValue, getValues } = useFormContext();
    const values = getValues();

    const { data: professionsData } = useQuery(GET_TALENT_PROFESSIONS_QUERY);
    const talentProfessions = useMemo(() => professionsData?.professions || [], [professionsData]);

    const [offerPremiumOpen, setOfferPremiumOpen] = React.useState(false);
    const [selectPremiumOptionOpen, setSelectPremiumOptionOpen] = React.useState(false);
    const isMobile = useMediaQuery('(max-width:600px)');

    // Watch for selected profession IDs
    const selectedProfessionIds = watch('position.professions');

    // Check if any selected profession has essaiRequired = true
    const hasEssaiRequired = useMemo(() => {
        if (!selectedProfessionIds || selectedProfessionIds.length === 0 || !talentProfessions.length) {
            return false;
        }

        // Map selected IDs to profession objects and check essaiRequired
        return selectedProfessionIds.some(professionId => {
            const profession = talentProfessions.find(p => p._id === professionId);
            return profession?.essaiRequired === true;
        });
    }, [selectedProfessionIds, talentProfessions]);

    const mainPremiumOption: PremiumOption = {
        name: 'Offre Premium',
        description: `Vous avez choisi de vous faire épauler par les équipes d'Upercut pour recruter votre futur Talent.`,
        price: {
            percentage: parseFloat((pro?.RBA_percentage * 100).toFixed(2)),
            description: 'Du salaire brut annuel',
        },
    };

    const disabledPremiumOptions = useMemo(() => {
        const features = [];
        if (pro?.config?.dismissalGuarantee) {
            features.push('dismissalGuarantee');
        }
        if (pro?.config?.resignationGuarantee) {
            features.push('resignationGuarantee');
        }
        return features;
    }, [pro?.config]);

    const disablePremiumChange = values?.recruitment?.method === 'premium' && !!values?._id;
    const { credits } = useJobOfferCredits(pro?._id);

    useEffect(() => {
        if (credits.length === 1) {
            setValue('featureId', credits[0]._id);
        }
    }, [credits, setValue]);

    return (
        <>
            <InputSwitchControlled
                title="Candidature express : réponse en 48H"
                control={control}
                name="recruitment.quickResponse"
                defaultValue={values?.recruitment?.quickResponse}
            />
            <InputSwitchControlled
                title="Entretien dans l’établissement"
                control={control}
                name="recruitment.place"
                defaultValue={values?.recruitment?.place}
            />
            <InputSwitchControlled
                title="Entretien en visio"
                control={control}
                name="recruitment.mode"
                defaultValue={values?.recruitment?.mode}
            />
            {hasEssaiRequired && (
                <InputSwitchControlled
                    title="Essai/business case demandé"
                    control={control}
                    name="recruitment.attempt"
                    defaultValue={values?.recruitment?.attempt}
                    endAdornment={
                        <Popover hover>
                            <Box sx={{ py: 1 }}>
                                <Text dark size={18} bold>
                                    Essai/business case demandé
                                </Text>
                            </Box>
                            <Box sx={{ py: 1 }}>
                                <Text dark>
                                    Extrait de votre convention collective relatif à l’essai professionnel (article
                                    7-1-1)
                                </Text>
                            </Box>
                            <Box sx={{ py: 1 }}>
                                <Text dark>
                                    L’embauche peut être précédée d’un essai professionnel qui consiste en une épreuve
                                    permettant à l’employeur de vérifier la qualification de la personne qui postule et
                                    son aptitude à occuper le poste demandé.
                                </Text>
                            </Box>
                            <Box sx={{ py: 1 }}>
                                <Text dark>
                                    Durée maximum :<Text primary> 1 journée</Text>
                                </Text>
                                <Text dark>
                                    Indemnité forfaitaire :{' '}
                                    <Text primary>7 x le taux horaire minimum garanti du poste à pourvoir.</Text>
                                </Text>
                            </Box>
                        </Popover>
                    }
                />
            )}

            {premiumUsers?.split(',').includes(pro?._id) && (
                <>
                    <InputMultiSelect
                        single
                        label="Mode de réception des candidatures"
                        placeholder="Comment recevoir les candidatures"
                        disabled={disablePremiumChange}
                        notClearable
                        items={options?.recruitmentMethods?.map(groupOption => ({
                            ...groupOption,
                            children: groupOption.children.filter(option => {
                                if (pro?.config?.recruitmentMethod === 'premium') {
                                    return option.id === 'premium';
                                }
                                return true;
                            }),
                        }))}
                        required
                        control={control}
                        onChange={value => {
                            if (value === 'direct_email') {
                                setOfferPremiumOpen(true);
                            } else {
                                setSelectPremiumOptionOpen(true);
                            }
                        }}
                        name="recruitment.method"
                        defaultValue={values?.recruitment?.method}
                        rules={{ required: 'Ce champ est requis' }}
                    />
                    <PremiumOfferDialog
                        open={offerPremiumOpen}
                        onClose={(e: any, reason: any) => {
                            if (reason === 'backdropClick') {
                                return;
                            }
                            setOfferPremiumOpen(false);
                        }}
                        onSkip={() => {
                            setOfferPremiumOpen(false);
                            setValue('recruitment.premiumFeatures', []);
                        }}
                        onConfirm={() => {
                            setValue('recruitment.method', 'premium');
                            setOfferPremiumOpen(false);
                            setSelectPremiumOptionOpen(true);
                        }}
                    />
                    <Controller
                        name="recruitment.premiumFeatures"
                        control={control}
                        render={({ field: { value, onChange } }) => {
                            const selectedPermiumOptions = permiumOptions.filter(option => value.includes(option.type));

                            return (
                                <Stack gap={2} mb={2}>
                                    {selectedPermiumOptions?.length > 0 && (
                                        <Stack spacing={1}>
                                            {[mainPremiumOption, ...selectedPermiumOptions].map((option, index) => (
                                                <OptionItem
                                                    onChange={(_, __) => {
                                                        setSelectPremiumOptionOpen(true);
                                                    }}
                                                    key={index}
                                                    data={option}
                                                    disabled={disablePremiumChange}
                                                    isSelected
                                                />
                                            ))}
                                        </Stack>
                                    )}
                                    <OptionItems
                                        selected={value}
                                        premiumOptionsRequired={pro?.config?.recruitmentMethod === 'premium'}
                                        disabledOptions={disabledPremiumOptions}
                                        onClose={() => {
                                            setSelectPremiumOptionOpen(false);
                                        }}
                                        open={selectPremiumOptionOpen}
                                        onChange={selectedPremiumIds => {
                                            if (selectedPremiumIds.length === 0) {
                                                setSelectPremiumOptionOpen(false);
                                                setValue('recruitment.method', 'direct_email');
                                            }
                                            onChange(selectedPremiumIds);
                                            setSelectPremiumOptionOpen(false);
                                        }}
                                    />
                                </Stack>
                            );
                        }}
                    />
                </>
            )}
            {/* Recruitment  Steps */}
            <FormGroup>
                <FormLabel sx={{ mb: 1 }}>
                    Étapes du processus de recrutement
                </FormLabel>
                <AddMoreWithReorder
                    name="recruitment.steps"
                    control={control}
                    defaultObject=""
                    addMoreText="Ajouter une étape"
                    render={({ index, deleteButton, moveUpButton, moveDownButton, isFirst, isLast }) => {
                        return (
                            <Controller
                                name={`recruitment.steps.${index}`}
                                control={control}
                                defaultValue=""
                                render={({ field: { value, onChange }, fieldState: { error } }) => (
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <Box sx={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: '50%',
                                            textAlign: 'center',
                                        }}> 
                                            <Typography variant="h6" fontWeight={400}>
                                                {index + 1}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                            {moveUpButton}
                                            {moveDownButton}
                                        </Box>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder={`Étape ${index + 1} (ex: Entretien téléphonique, Rencontre avec l'équipe)`}
                                            value={value}
                                            onChange={(e) => onChange(e.target.value)}
                                            error={!!error}
                                            helperText={error?.message}
                                        />
                                        {deleteButton}
                                    </Box>
                                )}
                            />
                        );
                    }}
                />
            </FormGroup>
        </>
    );
};

export default JobOfferRecruitmentForm;
